import { type RequestPayload, router } from "@inertiajs/core";
import { usePage } from "@inertiajs/react";
import { Divider, Fieldset, Select, Stack, TextInput, Tooltip } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMemo, useState } from "react";
import { getFullName, getYearString } from "../../helpers/stringHelper";
import type { Apprentice } from "../../models/apprentice";
import type { Coach } from "../../models/coach";
import { getOccupationOptions } from "../../models/enums/occupation";
import type { Group } from "../../models/group";
import { getYearContextOptions, type YearContext } from "../../models/yearContext";
import { SaveButton } from "../button/SaveButton";
import { TransferList } from "../TransferList/TransferList";

type GroupFormProps = {
  group?: Group;
  apprenticeOptions?: Apprentice[];
  yearContexts: YearContext[];
  coaches: Coach[];
  submitButtonLabel: string;
  onSubmit: (values: RequestPayload) => void;
};

type FormErrors = {
  year_context?: string[];
  name?: string[];
  responsible_coach?: string[];
  occupation?: string[];
};

export function GroupForm({ group, yearContexts, coaches, apprenticeOptions, onSubmit, submitButtonLabel }: GroupFormProps) {
  const { errors } = usePage<{ errors: FormErrors }>().props;

  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      name: group?.name,
      year_context_id: group?.year_context.id.toString(),
      responsible_coach_id: group?.coach?.id.toString(),
      occupation: group?.occupation,
      apprentice_ids: group ? group.apprentices.map((a) => a.id.toString()) : [],
    },
  });
  const mappedApprenticeOptions = useMemo(
    () =>
      apprenticeOptions?.map((a) => ({
        label: getFullName(a),
        value: a.id.toString(),
      })),
    [apprenticeOptions],
  );

  form.watch("year_context_id", ({ value }) =>
    router.reload({
      only: ["apprentice_options"],
      data: { year_context_id: value },
    }),
  );

  const [disableYearContext, setDisableYearContext] = useState(!!group?.apprentices?.length);
  form.watch("apprentice_ids", () => setDisableYearContext(!!form.getValues().apprentice_ids.length));

  return (
    <form onSubmit={form.onSubmit((values) => onSubmit(values))}>
      <Fieldset legend="Details">
        <Stack>
          <Stack maw="61rem">
            <TextInput label="Name" withAsterisk key={form.key("name")} {...form.getInputProps("name")} error={errors?.name} />
            <Tooltip label="Keine Lernende dürfen der Gruppe zugewiesen sein" disabled={!disableYearContext}>
              <Select
                withAsterisk
                data={getYearContextOptions(yearContexts)}
                key={form.key("year_context_id")}
                {...form.getInputProps("year_context_id")}
                clearable
                disabled={disableYearContext}
                error={errors?.year_context}
                label="Jahrgang"
              />
            </Tooltip>
            <Select
              data={coaches.map((coach) => ({
                value: coach.id.toString(),
                label: getFullName(coach),
              }))}
              key={form.key("responsible_coach_id")}
              clearable
              label="Verantwortlicher Coach"
              {...form.getInputProps("responsible_coach_id")}
              error={errors?.responsible_coach}
            />
            <Select
              withAsterisk
              label="Beruf"
              clearable
              data={getOccupationOptions()}
              key={form.key("occupation")}
              {...form.getInputProps("occupation")}
              error={errors?.occupation}
            />
          </Stack>
          <TransferList
            targetTitle="Lernende in der Gruppe"
            sourceTitle={`Lernende aus dem Jahrgang: ${getYearString(yearContexts.find((y) => y.id.toString() === form.getValues().year_context_id))}`}
            searchLabel="Suche"
            options={mappedApprenticeOptions}
            key={form.key("apprentice_ids")}
            {...form.getInputProps("apprentice_ids")}
          />
          <Divider />
          <SaveButton maw="61rem" label={submitButtonLabel} />
        </Stack>
      </Fieldset>
    </form>
  );
}
